import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { TranslationService } from '../i18n/translation.service';
import { scheduleOrphanedOverlayMaskSweep } from '../commons/utils/overlay-mask-sweep';


// Define the interface for components that use the CanDeactivateGuard
export interface CanComponentDeactivate {
  canDeactivate?: () => Observable<boolean> | Promise<boolean> | boolean;
  hasUnsavedChanges?: () => boolean;
  isSubmitting?: boolean;
  isDeleting?: boolean;
  form?: { dirty?: boolean };
  myForm?: { dirty?: boolean };
  shipmentForm?: { dirty?: boolean };
  transportUnitForm?: { dirty?: boolean };
  filterForm?: { dirty?: boolean };
  businessPartnerForm?: { dirty?: boolean };
}

@Injectable({
  providedIn: 'root'
})
export class CanDeactivateGuard implements CanDeactivate<CanComponentDeactivate> {
  private readonly logger = inject(NGXLogger);

  constructor(
    private confirmationService: ConfirmationService,
    private translationService: TranslationService
  ) {}

  canDeactivate(component: CanComponentDeactivate): Observable<boolean> | Promise<boolean> | boolean {
    this.logger.debug('[CanDeactivateGuard] canDeactivate fired for', component?.constructor?.name);

    if (component.canDeactivate) {
      this.logger.debug('[CanDeactivateGuard] Delegating to component.canDeactivate()');
      return component.canDeactivate();
    }

    if (component.isSubmitting || component.isDeleting) {
      this.logger.debug('[CanDeactivateGuard] isSubmitting/isDeleting=true → allowing navigation');
      return true;
    }

    const hasChanges = component.hasUnsavedChanges
      ? component.hasUnsavedChanges()
      : this.hasDirtyKnownForm(component);

    this.logger.debug('[CanDeactivateGuard] hasUnsavedChanges =', hasChanges);

    if (!hasChanges) {
      return true;
    }

    return new Promise((resolve) => {
      this.confirmationService.confirm({
        key: 'routeGuard',
        header: this.translationService.instant('common.unsavedChangesHeader'),
        message: this.translationService.instant('common.unsavedChangesMessage'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translationService.instant('common.leaveLabel'),
        rejectLabel: this.translationService.instant('common.stayLabel'),
        // Explicitly close the dialog synchronously before resolving — same preventive pattern
        // as SideBarComponent's isMenuExpand=false-before-navigateByUrl() fix (see
        // offline-freeze-root-cause / overlay-mask-sweep.ts). Offline, the destination route's
        // guards (AuthSessionGuard etc.) fast-fail near-instantly, so the router's continuation
        // after resolve(true) can render the next page in the same frames as this dialog's own
        // ~300ms leave animation, cancelling it before animationend fires and leaving an
        // invisible, tap-swallowing mask behind. AppComponent's NavigationEnd-triggered sweep
        // already bounds that to ~800ms as a safety net (the "takes too long to close"
        // symptom); explicitly closing here first, and sweeping on BOTH branches (not just the
        // navigate-away Leave case — Stay never fires NavigationEnd at all, so it had zero
        // sweep coverage before this), gives every path the same protection.
        accept: () => {
          this.logger.debug('[CanDeactivateGuard] User chose Leave');
          this.confirmationService.close();
          scheduleOrphanedOverlayMaskSweep();
          resolve(true);
        },
        reject: () => {
          this.logger.debug('[CanDeactivateGuard] User chose Stay');
          this.confirmationService.close();
          scheduleOrphanedOverlayMaskSweep();
          resolve(false);
        }
      });
    });
  }

  private hasDirtyKnownForm(component: CanComponentDeactivate): boolean {
    const dirty = [
      component.form,
      component.myForm,
      component.shipmentForm,
      component.transportUnitForm,
      component.filterForm,
      component.businessPartnerForm,
    ].some(form => form?.dirty === true);
    this.logger.debug('[CanDeactivateGuard] hasDirtyKnownForm =', dirty);
    return dirty;
  }
}
